import React, { useState } from 'react';
import { RiskConstructionData, RiskComponentInfo } from '../Helpers/utils';
import { translate } from '../types/EnumTranslations';
import "./WarningViewComponent.css";

interface NestedRiskConstructionProps {
    riskConstructions: RiskConstructionData[];
    buildingKey: string;
}

const AlertIcon: React.FC<{ size: string }> = ({ size }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
        <line x1="12" y1="9" x2="12" y2="13"/>
        <line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>
);

const NestedRiskConstruction: React.FC<NestedRiskConstructionProps> = ({ 
    riskConstructions, 
    buildingKey 
}) => {
    const [isMainExpanded, setIsMainExpanded] = useState(false);
    const [expandedRisks, setExpandedRisks] = useState<Set<string>>(new Set());

    const handleComponentClick = (component: RiskComponentInfo) => {
        // Open the appropriate component page in a new tab based on type
        let url = '';
        if (component.type === 'room') {
            url = `/building/${buildingKey}/room/${component.key}`;
        } else if (component.type === 'level') {
            url = `/building/${buildingKey}/level/${component.key}`;
        } else {
            // For regular components
            url = `/building/${buildingKey}/component/${component.key}`;
        }
        
        // Open in new tab
        window.open(url, '_blank');
    };

    const getComponentDisplayName = (component: RiskComponentInfo): string => {
        if (component.label) {
            return component.label;
        }
        
        // Fallback to type-specific naming
        if (component.type === 'room') {
            return `Rum ${component.key}`;
        } else if (component.type === 'level') {
            return `Våning ${component.key}`;
        } else {
            return translate(component.type) || component.type;
        }
    };

    const toggleRiskExpansion = (riskType: string) => {
        const newExpandedRisks = new Set(expandedRisks);
        if (newExpandedRisks.has(riskType)) {
            newExpandedRisks.delete(riskType);
        } else {
            newExpandedRisks.add(riskType);
        }
        setExpandedRisks(newExpandedRisks);
    };

    const totalComponents = riskConstructions.reduce((sum, risk) => sum + risk.componentCount, 0);

    return (
        <div className="hazard-section">
            <div className="alert-box alert-box-enhanced cursor-pointer hazard-text-color-orange">
                <div
                    className="alert-header"
                    onClick={() => setIsMainExpanded(!isMainExpanded)}
                >
                    <div className="alert-dot alert-dot-orange"/>
                    <AlertIcon size="28"/>
                    <h4 className="alert-title hazard-text-color-orange">
                        Riskkonstruktioner ({riskConstructions.length})
                    </h4>
                    <svg
                        className={`ml-auto transform transition-transform duration-200 ${isMainExpanded ? 'rotate-180' : ''}`}
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                    >
                        <path d="M6 9l6 6 6-6"/>
                    </svg>
                </div>

                {isMainExpanded && (
                    <div className="alert-content">
                        {riskConstructions.map((riskData, index) => (
                            <div key={`${riskData.riskType}-${index}`} style={{ marginBottom: '8px' }}>
                                {/* Individual risk header */}
                                <div 
                                    className="enhanced-hazard-item hazard-item"
                                    onClick={() => toggleRiskExpansion(riskData.riskType)}
                                    style={{ 
                                        cursor: 'pointer'
                                    }}
                                >
                                    <AlertIcon size="18"/>
                                    <div style={{ flex: 1 }}>
                                        <span className="hazard-text">
                                            {riskData.riskType}
                                        </span>
                                        <span style={{ marginLeft: '8px', color: '#666', fontSize: '0.875rem' }}>
                                            ({riskData.componentCount})
                                        </span>
                                    </div>
                                    <svg
                                        className={`transform transition-transform duration-200 ${expandedRisks.has(riskData.riskType) ? 'rotate-180' : ''}`}
                                        width="16"
                                        height="16"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                    >
                                        <path d="M6 9l6 6 6-6"/>
                                    </svg>
                                </div>

                                {/* Components list for this risk */}
                                {expandedRisks.has(riskData.riskType) && (
                                    <div style={{ 
                                        marginLeft: '20px', 
                                        marginTop: '8px',
                                        paddingLeft: '12px'
                                    }}>
                                        {riskData.components.map((component, componentIndex) => (
                                            <div 
                                                key={`${component.key}-${componentIndex}`}
                                                className="enhanced-hazard-item hazard-item"
                                                onClick={() => handleComponentClick(component)}
                                                style={{ 
                                                    cursor: 'pointer',
                                                    marginBottom: '4px',
                                                    backgroundColor: 'white',
                                                    border: '1px solid #e9ecef'
                                                }}
                                            >
                                                <AlertIcon size="16"/>
                                                <div>
                                                    <span className="hazard-text">
                                                        {getComponentDisplayName(component)}
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default NestedRiskConstruction;
